import React, { useRef, useState } from 'react';
import { Star } from 'lucide-react';
import toast from 'react-hot-toast';

interface Props {
  /** Newly-picked local files pending upload, indexed by slot (0..maxPhotos-1). */
  files: (File | null)[];
  /** Already-uploaded server photo URLs, indexed by slot (edit mode only). */
  existingUrls?: (string | null)[];
  onChange: (files: (File | null)[]) => void;
  label?: string;
  maxPhotos?: number;
  thumbSize?: number;
  /** 1-based slot of the display/primary photo. When `onPrimaryChange` is
   * provided, a star badge is shown on each thumbnail to pick the primary. */
  primarySlot?: number | null;
  onPrimaryChange?: (slot: number | null) => void;
}

/** Shared multi-photo picker: one dropzone for adding several photos at once,
 * rendered above a row of removable/replaceable thumbnails. Used by all
 * device/consumable/batch photo forms. */
const PhotoUploadGrid: React.FC<Props> = ({
  files, existingUrls = [], onChange, label = 'Photos', maxPhotos = 5, thumbSize = 72,
  primarySlot = null, onPrimaryChange,
}) => {
  const dropRef = useRef<HTMLInputElement>(null);
  const replaceRefs = useRef<Array<HTMLInputElement | null>>([]);
  const [isDragOver, setIsDragOver] = useState(false);

  const slotCount = Math.max(files.length, existingUrls.length, maxPhotos);
  const slots = Array.from({ length: slotCount }, (_, i) => ({
    existingUrl: existingUrls[i] ?? null,
    file: files[i] ?? null,
  })).filter(s => s.existingUrl || s.file);

  const occupiedCount = slots.length;
  const remaining = maxPhotos - occupiedCount;

  const firstEmptyIndices = (count: number): number[] => {
    const taken = new Set<number>();
    for (let i = 0; i < slotCount; i++) {
      if (existingUrls[i] || files[i]) taken.add(i);
    }
    const empties: number[] = [];
    for (let i = 0; i < maxPhotos && empties.length < count; i++) {
      if (!taken.has(i)) empties.push(i);
    }
    return empties;
  };

  const addFiles = (incoming: FileList | File[]) => {
    const picked = Array.from(incoming).filter(f => f.type.startsWith('image/'));
    if (picked.length === 0) return;

    const usable = picked.slice(0, remaining);
    if (picked.length > remaining) {
      toast.error(remaining > 0 ? `Only ${remaining} more photo(s) can be added (max ${maxPhotos}).` : `Maximum of ${maxPhotos} photos reached.`);
    }
    if (usable.length === 0) return;

    const targets = firstEmptyIndices(usable.length);
    const next = [...files];
    while (next.length < slotCount) next.push(null);
    targets.forEach((slotIdx, i) => { next[slotIdx] = usable[i]; });
    onChange(next);
  };

  const replaceAt = (index: number, file: File) => {
    const next = [...files];
    while (next.length <= index) next.push(null);
    next[index] = file;
    onChange(next);
  };

  const removeAt = (index: number) => {
    const next = [...files];
    if (index < next.length) next[index] = null;
    onChange(next);
    // If the removed slot was the display photo, clear the selection.
    if (onPrimaryChange && primarySlot === index + 1) onPrimaryChange(null);
  };

  const dropzoneStyle: React.CSSProperties = {
    border: `2px dashed ${isDragOver ? '#3b82f6' : '#cbd5e1'}`,
    borderRadius: '8px',
    padding: '0.85rem',
    textAlign: 'center',
    cursor: 'pointer',
    backgroundColor: isDragOver ? '#eff6ff' : '#f8fafc',
    color: '#94a3b8',
    fontSize: '0.8rem',
    transition: 'all 0.15s',
  };

  return (
    <div>
      {occupiedCount > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.6rem', marginBottom: '0.6rem' }}>
          {Array.from({ length: slotCount }, (_, i) => i).filter(i => existingUrls[i] || files[i]).map(i => {
            const isReplacing = !!existingUrls[i] && !!files[i];
            const isNewOnly = !existingUrls[i] && !!files[i];
            const canRemove = isReplacing || isNewOnly;
            const src = files[i] ? URL.createObjectURL(files[i] as File) : (existingUrls[i] as string);
            return (
              <div key={i} style={{ position: 'relative', width: thumbSize, height: thumbSize }}>
                <img
                  src={src}
                  alt={`${label} ${i + 1}`}
                  onClick={() => replaceRefs.current[i]?.click()}
                  title="Click to replace"
                  style={{ width: thumbSize, height: thumbSize, borderRadius: '6px', objectFit: 'cover', border: onPrimaryChange && primarySlot === i + 1 ? '2px solid #f59e0b' : isReplacing ? '2px solid #3b82f6' : '1px solid #e2e8f0', cursor: 'pointer', display: 'block' }}
                />
                {onPrimaryChange && (
                  <button
                    type="button"
                    onClick={e => { e.stopPropagation(); onPrimaryChange(i + 1); }}
                    title={primarySlot === i + 1 ? 'Display photo' : 'Set as display photo'}
                    style={{ position: 'absolute', top: -7, left: -7, width: 20, height: 20, borderRadius: '50%', background: 'white', border: '1px solid #e2e8f0', boxShadow: '0 1px 2px rgba(0,0,0,0.15)', cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                  >
                    <Star size={12} color="#f59e0b" fill={primarySlot === i + 1 ? '#f59e0b' : 'none'} />
                  </button>
                )}
                {isReplacing && (
                  <span style={{ position: 'absolute', bottom: 2, left: 2, background: 'rgba(37,99,235,0.9)', color: 'white', fontSize: '0.55rem', fontWeight: 700, padding: '1px 4px', borderRadius: '3px' }}>
                    new
                  </span>
                )}
                {canRemove && (
                  <button
                    type="button"
                    onClick={() => removeAt(i)}
                    title={isReplacing ? 'Cancel replacement' : 'Remove'}
                    style={{ position: 'absolute', top: -7, right: -7, width: 20, height: 20, borderRadius: '50%', background: '#ef4444', color: 'white', border: '2px solid white', cursor: 'pointer', fontSize: '0.7rem', lineHeight: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 }}
                  >
                    ×
                  </button>
                )}
                <input
                  type="file"
                  accept="image/*"
                  ref={el => { replaceRefs.current[i] = el; }}
                  style={{ display: 'none' }}
                  onChange={e => { if (e.target.files?.[0]) replaceAt(i, e.target.files[0]); }}
                />
              </div>
            );
          })}
        </div>
      )}

      {remaining > 0 && (
        <div
          onClick={() => dropRef.current?.click()}
          onDragOver={e => { e.preventDefault(); setIsDragOver(true); }}
          onDragLeave={() => setIsDragOver(false)}
          onDrop={e => { e.preventDefault(); setIsDragOver(false); if (e.dataTransfer.files?.length) addFiles(e.dataTransfer.files); }}
          style={dropzoneStyle}
        >
          <div style={{ fontSize: '1.3rem', marginBottom: '0.2rem' }}>📷</div>
          Click or drag to add {label.toLowerCase()} ({occupiedCount} of {maxPhotos} added)
          <input
            type="file"
            accept="image/*"
            multiple
            ref={dropRef}
            style={{ display: 'none' }}
            onChange={e => { if (e.target.files?.length) addFiles(e.target.files); e.target.value = ''; }}
          />
        </div>
      )}
    </div>
  );
};

export default PhotoUploadGrid;
